import type { PortfolioDomainCollections } from "./collection-defs";
import type { DashboardNormalizedResponse } from "./normalizers";
import type { QueryClient } from "@tanstack/query-core";
import { queryKeys } from "~/lib/query-keys";

const MAX_COLLECTION_CACHE_SIZE = 10;

export interface PortfolioGraphScope {
  cacheKey?: string;
  sessionScopeToken?: string;
}

export interface PortfolioGraphDetailStatus {
  hasPerformanceSeriesRows: boolean;
  positionsInSharesUiEnabled: boolean;
}

export type PortfolioGraphSummaryComponent =
  | "assetPrices"
  | "assets"
  | "dashboardAssetSummaries"
  | "positions"
  | "vaults";

export interface PortfolioGraphSummaryComponentStatus {
  assetIds: Set<string>;
  assetPriceAssetIds: Set<string>;
  components: Set<PortfolioGraphSummaryComponent>;
  dashboardAssetSummaryIds: Set<string>;
  positionIds: Set<string>;
  vaultIds: Set<string>;
}

export interface PortfolioGraphDashboardSeedPayload {
  assetPrices?: DashboardNormalizedResponse["assetPrices"];
  assets?: DashboardNormalizedResponse["assets"];
  cashSummaries?: DashboardNormalizedResponse["cashSummaries"];
  dashboardAssetSummaries?: DashboardNormalizedResponse["dashboardAssetSummaries"];
  performanceSeries?: DashboardNormalizedResponse["performanceSeries"];
  positions?: DashboardNormalizedResponse["positions"];
  portfolioInstances?: DashboardNormalizedResponse["portfolioInstances"];
  vaults?: DashboardNormalizedResponse["vaults"];
}

export interface PortfolioGraphCachedDashboardSeed {
  cachedAtMs: number;
  payload: PortfolioGraphDashboardSeedPayload;
  strategyInstanceIds: string[];
}

export interface PortfolioGraphHydrationState {
  dashboardSeeds: Map<string, PortfolioGraphCachedDashboardSeed>;
  details: Map<string, PortfolioGraphDetailStatus>;
  indexHydrations: Map<string, Promise<string[]>>;
  ownerPortfolioIds: Set<string>;
  summaries: Set<string>;
  summaryComponents: Map<string, PortfolioGraphSummaryComponentStatus>;
  summaryHydrations: Map<string, Promise<void>>;
}

export interface PortfolioGraphContext {
  collections: PortfolioDomainCollections;
  hydration: PortfolioGraphHydrationState;
}

interface PortfolioGraphEntry extends PortfolioGraphContext {
  cacheKey: string;
  sessionScopeToken: string;
  subscriberCount: number;
}

interface PortfolioGraphReadSpec<Result> {
  createCollections: () => PortfolioDomainCollections;
  kind: "read";
  read: (context: PortfolioGraphContext) => Result;
}

interface PortfolioGraphSubscribeSpec {
  createCollections: () => PortfolioDomainCollections;
  kind: "subscribe";
}

interface PortfolioGraphCustomInvalidateSpec<Result> {
  invalidate: (contexts: readonly PortfolioGraphContext[]) => Result;
  kind: "custom";
}

interface PortfolioGraphSettlementInvalidateSpec {
  invalidateDetail: (input: {
    portfolioId: string;
    sessionScopeToken?: string;
  }) => Promise<void>;
  invalidateSummaries: (input: { sessionScopeToken?: string }) => Promise<void>;
  kind: "settlement";
  portfolioIds: readonly string[];
  queryClient: QueryClient;
  scheduleBurst: (queryClient: QueryClient) => void;
}

function createHydrationState(): PortfolioGraphHydrationState {
  return {
    dashboardSeeds: new Map(),
    details: new Map(),
    indexHydrations: new Map(),
    ownerPortfolioIds: new Set(),
    summaries: new Set(),
    summaryComponents: new Map(),
    summaryHydrations: new Map(),
  };
}

class PortfolioGraphStore {
  readonly #entries = new Map<string, PortfolioGraphEntry>();

  ensureHydrated<Result>(
    scope: Required<PortfolioGraphScope>,
    spec: PortfolioGraphReadSpec<Result>,
  ): Result;
  ensureHydrated(
    scope: Required<PortfolioGraphScope>,
    spec: PortfolioGraphSubscribeSpec,
  ): () => void;
  ensureHydrated<Result>(
    scope: Required<PortfolioGraphScope>,
    spec: PortfolioGraphReadSpec<Result> | PortfolioGraphSubscribeSpec,
  ): Result | (() => void) {
    const entry = this.#getOrCreateEntry(scope, spec.createCollections);

    if (spec.kind === "read") {
      return spec.read(entry);
    }

    entry.subscriberCount += 1;
    let released = false;
    return () => {
      if (released) {
        return;
      }

      released = true;
      const currentEntry = this.#entries.get(scope.cacheKey);
      if (currentEntry === entry) {
        currentEntry.subscriberCount = Math.max(
          0,
          currentEntry.subscriberCount - 1,
        );
      }
      this.#evictToCapacity();
    };
  }

  invalidate<Result>(
    scope: PortfolioGraphScope,
    spec: PortfolioGraphCustomInvalidateSpec<Result>,
  ): Result;
  invalidate(
    scope: PortfolioGraphScope,
    spec: PortfolioGraphSettlementInvalidateSpec,
  ): Promise<void>;
  invalidate<Result>(
    scope: PortfolioGraphScope,
    spec:
      | PortfolioGraphCustomInvalidateSpec<Result>
      | PortfolioGraphSettlementInvalidateSpec,
  ): Result | Promise<void> {
    const contexts: PortfolioGraphContext[] = [];
    for (const entry of Array.from(this.#entries.values())) {
      if (!this.#matchesScope(entry, scope)) {
        continue;
      }

      this.#touch(entry);
      contexts.push(entry);
    }

    if (spec.kind === "custom") {
      return spec.invalidate(contexts);
    }

    for (const context of contexts) {
      context.hydration.summaries.clear();
      context.hydration.summaryComponents.clear();
      context.hydration.summaryHydrations.clear();
    }

    spec.scheduleBurst(spec.queryClient);
    return Promise.all([
      ...spec.portfolioIds.map((portfolioId) =>
        spec.invalidateDetail({
          portfolioId,
          sessionScopeToken: scope.sessionScopeToken,
        }),
      ),
      spec.invalidateSummaries({
        sessionScopeToken: scope.sessionScopeToken,
      }),
      spec.queryClient.invalidateQueries({ queryKey: queryKeys.portfolio.all }),
      spec.queryClient.invalidateQueries({
        queryKey: queryKeys.investingAccount.all,
      }),
    ]).then(() => undefined);
  }

  reset(scope: PortfolioGraphScope = {}): void {
    for (const [cacheKey, entry] of this.#entries) {
      if (this.#matchesScope(entry, scope)) {
        this.#entries.delete(cacheKey);
      }
    }
  }

  #getOrCreateEntry(
    scope: Required<PortfolioGraphScope>,
    createCollections: () => PortfolioDomainCollections,
  ): PortfolioGraphEntry {
    const existing = this.#entries.get(scope.cacheKey);
    if (existing) {
      this.#touch(existing);
      return existing;
    }

    this.#evictOneIfAtCapacity();
    const created: PortfolioGraphEntry = {
      cacheKey: scope.cacheKey,
      collections: createCollections(),
      hydration: createHydrationState(),
      sessionScopeToken: scope.sessionScopeToken,
      subscriberCount: 0,
    };
    this.#entries.set(scope.cacheKey, created);
    return created;
  }

  #touch(entry: PortfolioGraphEntry): void {
    if (this.#entries.get(entry.cacheKey) !== entry) {
      return;
    }

    this.#entries.delete(entry.cacheKey);
    this.#entries.set(entry.cacheKey, entry);
  }

  #evictOneIfAtCapacity(): void {
    if (this.#entries.size < MAX_COLLECTION_CACHE_SIZE) {
      return;
    }

    for (const [cacheKey, entry] of this.#entries) {
      if (entry.subscriberCount === 0) {
        this.#entries.delete(cacheKey);
        return;
      }
    }
  }

  #evictToCapacity(): void {
    while (this.#entries.size > MAX_COLLECTION_CACHE_SIZE) {
      let evicted = false;
      for (const [cacheKey, entry] of this.#entries) {
        if (entry.subscriberCount > 0) {
          continue;
        }

        this.#entries.delete(cacheKey);
        evicted = true;
        break;
      }

      if (!evicted) {
        return;
      }
    }
  }

  #matchesScope(
    entry: PortfolioGraphEntry,
    scope: PortfolioGraphScope,
  ): boolean {
    if (scope.cacheKey && entry.cacheKey !== scope.cacheKey) {
      return false;
    }

    return (
      scope.sessionScopeToken === undefined ||
      entry.sessionScopeToken === scope.sessionScopeToken
    );
  }
}

export const portfolioGraphStore = new PortfolioGraphStore();
